import { Controller } from 'stimulus'

// Subscribes to the per-thread balance messaging websocket and appends
// inbound message bubbles into `#balance-messages`. The send-form's
// Turbo Stream response also inserts the sender's own bubble immediately,
// so this controller dedupes by `data-message-id` to avoid double-render.
//
// Connects to `/ws/balance/chat/<thread_uuid>/`. The server pushes JSON
// of the form `{ action: 'new_message', message_id: number, html: string }`.
export default class extends Controller {
  static values = { threadUuid: String }
  static targets = ['modal', 'modalImage']

  declare threadUuidValue: string
  declare readonly hasModalTarget: boolean
  declare readonly modalTarget: HTMLDialogElement
  declare readonly modalImageTarget: HTMLImageElement

  socket: WebSocket | null = null
  reconnectTimer: number | null = null
  dedupeObserver: MutationObserver | null = null

  // Open the tapped message image full screen in the native <dialog>.
  openImage(event: Event & { params: { url: string } }) {
    if (!this.hasModalTarget) return
    this.modalImageTarget.src = event.params.url
    this.modalTarget.showModal()
  }

  // Close the viewer (backdrop or image tap) and drop the src so the image
  // isn't held in memory while the dialog is hidden.
  closeImage() {
    if (!this.hasModalTarget) return
    this.modalTarget.close()
    this.modalImageTarget.removeAttribute('src')
  }

  connect() {
    // The sender's bubble can arrive twice — once from the send form's Turbo
    // Stream APPEND and once from the websocket echo — and whichever loses the
    // race slips past the per-insert check below. Watch the container and drop
    // any bubble whose message id is already present, regardless of order.
    const container = document.getElementById('balance-messages')
    if (container) {
      this.dedupeObserver = new MutationObserver(() =>
        this.dedupeBubbles(container),
      )
      this.dedupeObserver.observe(container, { childList: true })
    }
    this.openSocket()
  }

  disconnect() {
    if (this.dedupeObserver) {
      this.dedupeObserver.disconnect()
      this.dedupeObserver = null
    }
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.socket) {
      this.socket.onclose = null
      this.socket.close()
      this.socket = null
    }
  }

  private dedupeBubbles(container: HTMLElement) {
    const seen = new Set<string>()
    container.querySelectorAll('[data-message-id]').forEach((bubble) => {
      const id = bubble.getAttribute('data-message-id') ?? ''
      if (seen.has(id)) {
        bubble.remove()
      } else {
        seen.add(id)
      }
    })
  }

  private openSocket() {
    const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws'
    const url = `${scheme}://${window.location.host}/ws/balance/chat/${this.threadUuidValue}/`
    const socket = new WebSocket(url)
    this.socket = socket

    socket.onmessage = (event: MessageEvent) => {
      this.handleMessage(event.data)
    }

    socket.onclose = () => {
      this.socket = null
      this.scheduleReconnect()
    }

    socket.onerror = () => {
      socket.close()
    }
  }

  private scheduleReconnect() {
    if (this.reconnectTimer !== null) return
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null
      this.openSocket()
    }, 3000)
  }

  private handleMessage(raw: unknown) {
    if (typeof raw !== 'string') return
    let payload: { action?: string; message_id?: number; html?: string }
    try {
      payload = JSON.parse(raw)
    } catch {
      return
    }
    if (
      payload.action !== 'new_message' ||
      !payload.html ||
      payload.message_id === undefined
    ) {
      return
    }

    const container = document.getElementById('balance-messages')
    if (!container) return

    const existing = container.querySelector(
      `[data-message-id="${payload.message_id}"]`,
    )
    if (existing) return

    container.insertAdjacentHTML('beforeend', payload.html)
    container.scrollIntoView({ block: 'end', behavior: 'smooth' })
  }
}
